import axios from "axios";
import { baseUrl, getToken } from "./baseRequest";
import {
  Ticket,
  TicketStatus,
  TicketType,
  TicketPriority,
  Subtask,
  TicketComment,
  Attachment,
} from "../views/Tickets/types";

const TICKETS_API_KEY =
  process.env.REACT_APP_TICKETS_API_KEY ?? "d57eda70ecd57b806bebedc9d3379297";

const ticketsHeaders = async () => ({
  Authorization: `Bearer ${await getToken()}`,
  "x-tickets-api-key": TICKETS_API_KEY,
});

const ticketsApi = {
  get: async <T>(url: string, params?: object): Promise<T> => {
    const response = await axios.get(baseUrl + url, {
      headers: await ticketsHeaders(),
      params,
    });
    return response.data;
  },
  post: async <T>(url: string, body?: object): Promise<T> => {
    const response = await axios.post(baseUrl + url, body, {
      headers: await ticketsHeaders(),
    });
    return response.data;
  },
  put: async <T>(url: string, body?: object): Promise<T> => {
    const response = await axios.put(baseUrl + url, body, {
      headers: await ticketsHeaders(),
    });
    return response.data;
  },
  delete: async <T>(url: string): Promise<T> => {
    const response = await axios.delete(baseUrl + url, {
      headers: await ticketsHeaders(),
    });
    return response.data;
  },
};

// One AND-ed label condition: a ticket matches when it carries at least one of
// `anyOf`, or has no labels at all when `includeUnlabeled` is set.
export type LabelCondition = {
  anyOf?: string[];
  includeUnlabeled?: boolean;
};

export type TicketSearchFilters = {
  statuses?: TicketStatus[];
  excludeStatuses?: TicketStatus[];
  types?: TicketType[];
  priorities?: TicketPriority[];
  labelConditions?: LabelCondition[];
  // Tickets store either a user id or a user name in these fields, so every
  // alias of the selected users has to be sent.
  assignees?: string[];
  includeUnassigned?: boolean;
  assigneeSearch?: string;
  reporters?: string[];
  targetVersion?: string;
  updatedFrom?: string;
  updatedTo?: string;
  isArchived?: boolean;
  isBacklog?: boolean;
  searchCreators?: string[];
};

export type SearchTicketsInput = {
  query?: string;
  limit?: number;
  skip?: number;
  sort?: Record<string, 1 | -1>;
  filters?: TicketSearchFilters;
};

export type LabelStats = {
  label: string;
  total: number;
  done: number;
  inProgress: number;
  backlog: number;
};

export type FacetValue = {
  value: string;
  count: number;
};

export type TicketFacets = {
  labelStats: LabelStats[];
  unlabeledCount: number;
  assignees: FacetValue[];
  reporters: FacetValue[];
  total: number;
};

export type CreateTicketInput = {
  title: string;
  description?: string;
  status?: TicketStatus;
  type?: TicketType;
  priority?: TicketPriority;
  assignee?: string;
  labels?: string[];
  dueDate?: string | null;
  agentInput?: string;
  targetVersion?: string;
  isBacklog?: boolean;
  creatorId?: string;
  url?: string;
  shopifyPage?: string;
};

const ticketsClient = {
  searchTickets: async (
    input: SearchTicketsInput,
  ): Promise<{ tickets: Ticket[]; total: number }> => {
    const { query = "", ...body } = input;
    return ticketsApi.post<{ tickets: Ticket[]; total: number }>(
      `/admin/tickets/.search?query=${encodeURIComponent(query)}`,
      body,
    );
  },

  getTicketFacets: async (
    filters: TicketSearchFilters,
  ): Promise<TicketFacets> =>
    ticketsApi.post<TicketFacets>("/admin/tickets/.facets", { filters }),

  getTicket: async (id: string): Promise<Ticket> => {
    return ticketsApi.get<Ticket>(`/admin/tickets/${id}`);
  },

  createTicket: async (input: CreateTicketInput): Promise<Ticket> => {
    return ticketsApi.post<Ticket>("/admin/tickets", input);
  },

  updateTicket: async (
    id: string,
    updates: Partial<Ticket>,
  ): Promise<Ticket> => {
    return ticketsApi.put<Ticket>(`/admin/tickets/${id}`, updates);
  },

  deleteTicket: async (id: string): Promise<void> => {
    await ticketsApi.delete(`/admin/tickets/${id}`);
  },

  addComment: async (
    ticketId: string,
    comment: TicketComment,
  ): Promise<Ticket> => {
    return ticketsApi.post<Ticket>(
      `/admin/tickets/${ticketId}/comments`,
      comment,
    );
  },

  addSubtask: async (ticketId: string, subtask: Subtask): Promise<Ticket> => {
    return ticketsApi.post<Ticket>(
      `/admin/tickets/${ticketId}/subtasks`,
      subtask,
    );
  },

  toggleSubtask: async (
    ticketId: string,
    subtaskId: string,
  ): Promise<Ticket> => {
    return ticketsApi.put<Ticket>(
      `/admin/tickets/${ticketId}/subtasks/${subtaskId}/toggle`,
      {},
    );
  },

  updateSubtask: async (
    ticketId: string,
    subtaskId: string,
    updates: { title: string },
  ): Promise<Ticket> => {
    return ticketsApi.put<Ticket>(
      `/admin/tickets/${ticketId}/subtasks/${subtaskId}`,
      updates,
    );
  },

  deleteSubtask: async (
    ticketId: string,
    subtaskId: string,
  ): Promise<Ticket> => {
    return ticketsApi.delete<Ticket>(
      `/admin/tickets/${ticketId}/subtasks/${subtaskId}`,
    );
  },

  reorderSubtasks: async (
    ticketId: string,
    subtaskIds: string[],
  ): Promise<Ticket> => {
    return ticketsApi.put<Ticket>(
      `/admin/tickets/${ticketId}/subtasks/reorder`,
      { subtaskIds },
    );
  },

  addAttachment: async (
    ticketId: string,
    attachment: Attachment,
  ): Promise<Ticket> => {
    return ticketsApi.post<Ticket>(
      `/admin/tickets/${ticketId}/attachments`,
      attachment,
    );
  },

  deleteAttachment: async (
    ticketId: string,
    attachmentId: string,
  ): Promise<Ticket> => {
    return ticketsApi.delete<Ticket>(
      `/admin/tickets/${ticketId}/attachments/${attachmentId}`,
    );
  },
};

export default ticketsClient;
